import { rootApi, getGatewayQuery } from '@web/utils/@reduxjs/rtk-query';

import { Address } from '@web/types/common/address';
import { Parcel } from '@web/types/order/parcel';
import {
  Changelog,
  Country,
  CustomerPreferences,
  Customer,
  Marketplace,
  Carrier,
  User,
  PlanInfo,
} from '@web/types/global';
import {
  SwitchPlatformTogglePayload,
  SwitchPlatformToggleResult,
  GetAddressBookQueryPayload,
  GetChangelogMutationPayload,
  GlobalAPIActions,
  GlobalSecondaryDataPayload,
  GlobalSecondaryDataResult,
  CheckAvailableFeatureQueryResult,
  CheckAvailableFeatureQueryPayload,
  HasActivePlanQueryResult,
  HasActivePlanQueryPayload,
} from './types';
import {
  FetchedOnboarding,
  FetchedSetup,
  ListCarriersQueryPayload,
  ListCarriersQueryResult,
  ListMarketplacesQueryPayload,
  ListMarketplacesQueryResult,
} from '@web/features/_global/types';

// Define a service using a base URL and expected endpoints
export const globalApi = rootApi.injectEndpoints({
  endpoints: build => ({
    // string is the type of the returned response
    // void is the type of the parameters (this one has no params)
    getHealthCheck: build.query<string, void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(GlobalAPIActions.HealthCheck),
    }),
    /**
     * API: getGlobalSecondaryData
     *
     * @provides [{ type: 'BulkAction', id: 'The passed bulk ID' }]
     */
    getGlobalSecondaryData: build.query<GlobalSecondaryDataResult, string>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, GlobalSecondaryDataPayload>(
          GlobalAPIActions.BulkMultipleAction,
          {
            [GlobalAPIActions.GetInvoicingInfo]: {
              action: GlobalAPIActions.GetInvoicingInfo,
              payload: {},
            },
            [GlobalAPIActions.UserGetEmailServers]: {
              action: GlobalAPIActions.UserGetEmailServers,
              payload: {},
            },
            [GlobalAPIActions.UserGetPlugins]: {
              action: GlobalAPIActions.UserGetPlugins,
              payload: {},
            },
          },
        ),
      providesTags: (_, __, id) => [{ type: 'BulkAction', id }],
    }),
    getCustomerPreferences: build.query<CustomerPreferences, void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(
          GlobalAPIActions.CustomerGetPreferences,
        ),
    }),
    getChangelog: build.query<Changelog, GetChangelogMutationPayload>({
      query: ({ page }) =>
        getGatewayQuery<GlobalAPIActions, GetChangelogMutationPayload>(
          GlobalAPIActions.PortalGetChangelog,
          { page },
        ),
      providesTags: (_, __, { page }) => [{ type: 'Changelog', id: page }],
    }),
    getCountries: build.query<Country[], void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(GlobalAPIActions.GetCountries),
    }),
    getSenderInfo: build.query<Customer['senderInfo'], void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(
          GlobalAPIActions.GetCustomerSenderInfo,
        ),
    }),
    getMarketplaces: build.query<Marketplace[], void>({
      query: payload =>
        getGatewayQuery<GlobalAPIActions, void>(
          GlobalAPIActions.GetMarketplaces,
          payload,
        ),
      providesTags: ['Marketplaces'],
    }),
    getCarriers: build.query<Carrier[], void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(GlobalAPIActions.GetCarriers),
      providesTags: ['Carriers'],
    }),
    getUser: build.query<User, void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(GlobalAPIActions.GetUserInfo),
      providesTags: ['User'],
    }),
    getPlanInfo: build.query<PlanInfo, void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(GlobalAPIActions.GetPlanInfo),
      providesTags: ['Plan'],
    }),
    getAddressBook: build.query<Address[], GetAddressBookQueryPayload>({
      query: ({ address_type }) =>
        getGatewayQuery<GlobalAPIActions, GetAddressBookQueryPayload>(
          GlobalAPIActions.CustomerGetAddressBook,
          { address_type },
        ),
      providesTags: ['AddressBook'],
    }),
    getHotJarFields: build.query<
      Record<string | number, string | number | Date | boolean>,
      void
    >({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(
          GlobalAPIActions.UserGetHotJarFields,
        ),
    }),
    getRefinerFields: build.query<
      Record<string | number, string | number | Date | boolean>,
      void
    >({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(
          GlobalAPIActions.UserGetRefinerFields,
        ),
    }),
    getFeatureFlaggingCustomParameters: build.query<
      Record<string | number, string | number | Date | boolean>,
      void
    >({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(
          GlobalAPIActions.UserGetFeatureFlaggingCustomParameters,
        ),
    }),
    getParcelBook: build.query<Parcel[], void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(
          GlobalAPIActions.UserGetParcelsBook,
        ),
      providesTags: ['Parcel'],
    }),
    /**
     * API: switchPlatformToggle
     *
     */
    switchPlatformToggle: build.mutation<
      SwitchPlatformToggleResult,
      SwitchPlatformTogglePayload
    >({
      query: () =>
        getGatewayQuery<GlobalAPIActions, SwitchPlatformTogglePayload>(
          GlobalAPIActions.UserSwitchPlatformToggle,
          { toggle_status: 0 },
        ),
    }),
    /**
     * API: getSetupProgress
     *
     * @provides ['Setup']
     */
    getSetupProgress: build.query<FetchedSetup, void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(GlobalAPIActions.GetSetup),
      providesTags: () => ['Setup'],
    }),
    /**
     * API: getOnboardingProgress
     *
     * @provides ['Onboarding']
     */
    getOnboardingProgress: build.query<FetchedOnboarding, void>({
      query: () =>
        getGatewayQuery<GlobalAPIActions, void>(GlobalAPIActions.GetOnboarding),
      providesTags: () => ['Onboarding'],
    }),
    /**
     * API: listCarriers
     *
     */
    listCarriers: build.query<
      ListCarriersQueryResult,
      ListCarriersQueryPayload | undefined
    >({
      query: args =>
        getGatewayQuery<GlobalAPIActions, ListCarriersQueryPayload>(
          GlobalAPIActions.ListCarriers,
          args,
        ),
    }),
    /**
     * API: listMarketplaces
     *
     */
    listMarketplaces: build.query<
      ListMarketplacesQueryResult,
      ListMarketplacesQueryPayload | undefined
    >({
      query: args =>
        getGatewayQuery<GlobalAPIActions, ListMarketplacesQueryPayload>(
          GlobalAPIActions.ListMarketplaces,
          args,
        ),
    }),
    /**
     * API: checkAvailableFeature
     *
     */
    checkAvailableFeature: build.query<
      CheckAvailableFeatureQueryResult,
      CheckAvailableFeatureQueryPayload
    >({
      query: args =>
        getGatewayQuery<GlobalAPIActions, CheckAvailableFeatureQueryPayload>(
          GlobalAPIActions.CheckAvailableFeature,
          args,
        ),
    }),
    /**
     * API: UserHasActivePlan
     *
     */
    hasActivePlan: build.query<
      HasActivePlanQueryResult,
      HasActivePlanQueryPayload
    >({
      query: args =>
        getGatewayQuery<GlobalAPIActions, HasActivePlanQueryPayload>(
          GlobalAPIActions.HasActivePlan,
          args,
        ),
    }),
    // [APPEND NEW APIS ABOVE] < Needed for generating API Hooks seamlessly
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints ("use" + endpoint name with capital letter)
export const {
  useGetHealthCheckQuery,
  useGetGlobalSecondaryDataQuery,
  useGetCustomerPreferencesQuery,
  useLazyGetChangelogQuery,
  useGetCountriesQuery,
  useGetSenderInfoQuery,
  useGetMarketplacesQuery,
  useGetCarriersQuery,
  useGetUserQuery,
  useGetPlanInfoQuery,
  useGetAddressBookQuery,
  useGetHotJarFieldsQuery,
  useGetRefinerFieldsQuery,
  useGetFeatureFlaggingCustomParametersQuery,
  useGetParcelBookQuery,
  useSwitchPlatformToggleMutation,
  useGetOnboardingProgressQuery,
  useGetSetupProgressQuery,
  useListCarriersQuery,
  useListMarketplacesQuery,
  useCheckAvailableFeatureQuery,
  useHasActivePlanQuery,
  // [EXPORT NEW APIS ABOVE] < Needed for generating API Hooks seamlessly
} = globalApi;
